import { useState } from "react";
import { Page, StickyStackItem } from "@common";
import ClinicCashFlow from "./ClinicCashFlow";
import SchedulePayment from "./SchedulePayment";
import DailyBankBalance from "./DailyBankBalance";
import Actions from "./actions/Actions";

// All Cash Flow tabs, in display order. Every role that reaches this route sees
// all of them.
const TAB_OPTIONS = [
  { key: "cashFlow", label: "Cash Flow" },
  { key: "schedulePayment", label: "Schedule Payment" },
  { key: "dailyBankBalance", label: "Daily Balance" },
  { key: "actions", label: "Actions" },
];

/**
 * Tab container for the Cash Flow page.
 *
 * This is the page shell: it owns the tab bar and renders each tab's own
 * component. It decides neither clinic scoping nor write access.
 *
 * Tab visibility: every role the route admits (Organizer / Admin / Payroll /
 * Bookkeeper / Finance Coordinator) sees all four tabs. Payroll used to be
 * narrowed to Daily Bank Balance because it is absent from backend
 * `can_read_clinic_data`, so the other tabs 403'd on read. That is fixed at the
 * source: `schedule_payment` / `statement_payment` / `statement_plan` now use
 * `ClinicBasedCRUDWithPayrollPermission` and `forecasts` uses the read-only
 * variant, so don't reintroduce a role filter here.
 *
 * Clinic scoping is NOT uniform, despite every role here being all-clinic in the
 * route gate. Organizer/Admin/Payroll/Finance Coordinator are all-clinic on every
 * resource, but **Bookkeeper is clinic-scoped on two of them**, because the
 * backend views disagree about which helper to use:
 *   - schedule_payment, daily_bank_balance → unscoped querysets → all clinics.
 *   - forecasts, transfer → `get_accessible_clinics`, which returns
 *     `list(user.clinic)` for role 8 → assigned clinics only.
 * So a Bookkeeper's Cash Flow grid mixes all-clinic balances/payments with
 * own-clinic revenue and transfers. That inconsistency is a backend scoping
 * question (`get_accessible_clinics` vs `get_accessible_finance_clinics`, where
 * role 8 IS all-clinic) — don't try to paper over it in the frontend.
 *
 * Write access is NOT decided here. Each tab hits a different backend resource
 * with a different write set, so each one derives its own `canEdit` from
 * `canManageSchedulePayments` / `canManageDailyBankBalance` /
 * `canManageTransfers` in utils/roles.js (each mirrors a backend capability —
 * keep them in sync). Bookkeeper now writes Schedule Payment (create, edit,
 * cancel, delete) and Daily Bank Balance across all clinics, and stays
 * read-only on transfers. Every tab also accepts an optional `canEdit` prop so
 * a host can embed it read-only; none is passed here.
 */
export default function AccessClinicCashFlow() {
  const [activeTab, setActiveTab] = useState(TAB_OPTIONS[0].key);

  return (
    <Page>
      {/* Tab bar */}
      <StickyStackItem className="-mx-6 px-6">
        <div className="mb-4 flex gap-1 border-b border-palette-slate-divider">
          {TAB_OPTIONS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`-mb-px border-b-2 px-3 py-1.5 text-sm font-medium transition-colors ${
                activeTab === t.key
                  ? "border-palette-indigo-label text-palette-indigo-badge"
                  : "border-transparent text-palette-slate-secondary hover:text-palette-slate-heading"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </StickyStackItem>

      {activeTab === "schedulePayment" ? (
        <SchedulePayment />
      ) : activeTab === "dailyBankBalance" ? (
        <DailyBankBalance />
      ) : activeTab === "actions" ? (
        <Actions />
      ) : (
        <ClinicCashFlow />
      )}
    </Page>
  );
}
